<template>
    <div class="l2u-login-redirect">
        <!-- Route paths background SVG -->
        <svg class="route-paths-svg" viewBox="0 0 500 700" preserveAspectRatio="none">
            <defs>
                <linearGradient id="routeGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="rgba(43,147,255,0.12)" />
                    <stop offset="100%" stop-color="rgba(43,147,255,0.03)" />
                </linearGradient>
                <linearGradient id="routeGrad2" x1="100%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stop-color="rgba(78,210,255,0.1)" />
                    <stop offset="100%" stop-color="rgba(78,210,255,0.02)" />
                </linearGradient>
            </defs>
            <path d="M 60 0 Q 60 180, 200 250 T 400 400 T 300 700"
                  fill="none" stroke="url(#routeGrad1)" stroke-width="1.5"
                  stroke-dasharray="8 6" class="route-dash" />
            <path d="M 350 0 Q 350 120, 250 200 T 100 380 T 200 700"
                  fill="none" stroke="url(#routeGrad2)" stroke-width="1.5"
                  stroke-dasharray="8 6" class="route-dash route-dash--slow" />
            <path d="M 450 100 Q 400 250, 280 300 T 50 500"
                  fill="none" stroke="rgba(43,147,255,0.08)" stroke-width="1"
                  stroke-dasharray="4 8" class="route-dash route-dash--slower" />
            <circle cx="200" cy="250" r="3" fill="rgba(43,147,255,0.4)" class="waypoint" />
            <circle cx="350" cy="120" r="3" fill="rgba(78,210,255,0.35)" class="waypoint waypoint--d1" />
            <circle cx="100" cy="380" r="3" fill="rgba(43,147,255,0.3)" class="waypoint waypoint--d2" />
            <circle cx="400" cy="400" r="3" fill="rgba(78,210,255,0.3)" class="waypoint waypoint--d3" />
        </svg>

        <!-- Center card -->
        <div class="redirect-card">
            <img class="card-logo" src="/img/icons/locate2u-logo.png" alt="Locate2u" />

            <!-- Spinner / Success -->
            <div :class="['spinner-ring', { done: allDone }]">
                <svg v-if="allDone" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="8 12 11 15 16 9" />
                </svg>
                <svg v-else width="72" height="72" viewBox="0 0 72 72" class="spinner-arc">
                    <circle cx="36" cy="36" r="32" fill="none" stroke="#EAF2FB" stroke-width="3" />
                    <circle cx="36" cy="36" r="32" fill="none" stroke="#2B93FF" stroke-width="3"
                            stroke-linecap="round" stroke-dasharray="60 141" class="arc" />
                </svg>
            </div>

            <h2 class="heading">{{ heading }}</h2>
            <p class="status-text">{{ statusMessage }}</p>

            <!-- Progress bar -->
            <div class="progress-track">
                <div :class="['progress-bar', { done: allDone }]"
                     :style="{ width: progressPercent + '%' }"></div>
            </div>

            <!-- Steps -->
            <div class="steps">
                <div v-for="(step, i) in steps" :key="i"
                     :class="['step', { active: currentStep === i, done: currentStep > i }]">
                    <div class="step-indicator">
                        <!-- Checkmark for done steps -->
                        <svg v-if="currentStep > i" width="14" height="14" viewBox="0 0 24 24"
                             fill="none" stroke="#059669" stroke-width="3">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <!-- Lock icon -->
                        <svg v-else-if="step.icon === 'lock'" width="18" height="18" viewBox="0 0 24 24"
                             fill="none" :stroke="currentStep === i ? '#2B93FF' : '#C5CFDD'" stroke-width="2">
                            <rect x="3" y="11" width="18" height="11" rx="2" />
                            <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                        <!-- Grid icon -->
                        <svg v-else-if="step.icon === 'grid'" width="18" height="18" viewBox="0 0 24 24"
                             fill="none" :stroke="currentStep === i ? '#2B93FF' : '#C5CFDD'" stroke-width="2">
                            <rect x="3" y="3" width="7" height="7" rx="1" />
                            <rect x="14" y="3" width="7" height="7" rx="1" />
                            <rect x="3" y="14" width="7" height="7" rx="1" />
                            <rect x="14" y="14" width="7" height="7" rx="1" />
                        </svg>
                        <!-- Route icon -->
                        <svg v-else-if="step.icon === 'route'" width="18" height="18" viewBox="0 0 24 24"
                             fill="none" :stroke="currentStep === i ? '#2B93FF' : '#C5CFDD'" stroke-width="2">
                            <circle cx="6" cy="6" r="3" />
                            <circle cx="18" cy="18" r="3" />
                            <path d="M6 9v2a4 4 0 004 4h4a4 4 0 014 4v-1" />
                        </svg>
                    </div>
                    <span class="step-label">{{ step.label }}</span>
                    <div v-if="currentStep === i && currentStep < steps.length" class="active-dot"></div>
                </div>
            </div>
        </div>

        <p class="bottom-trust">Locate2u — Route optimisation and delivery management</p>
    </div>
</template>

<script>
export default {
    name: 'LoginRedirect',
    props: {
        currentStep: {
            type: Number,
            default: 0
        },
        allDone: {
            type: Boolean,
            default: false
        },
        mode: {
            type: String,
            default: 'login' // 'login' or 'logout'
        }
    },
    data() {
        return {
            dots: ''
        };
    },
    computed: {
        steps() {
            if (this.mode === 'logout') {
                return [
                    { label: 'Signing out', icon: 'lock' },
                    { label: 'Clearing session', icon: 'grid' }
                ];
            }
            return [
                { label: 'Authenticating', icon: 'lock' },
                { label: 'Loading your workspace', icon: 'grid' },
                { label: 'Preparing routes', icon: 'route' }
            ];
        },
        heading() {
            if (this.allDone) return this.mode === 'logout' ? 'Signed out' : 'You are all set';
            return this.mode === 'logout' ? 'Signing you out' : 'Signing you in';
        },
        progressPercent() {
            if (this.allDone) return 100;
            if (this.mode === 'logout') {
                const logoutTargets = [30, 70];
                return logoutTargets[Math.min(this.currentStep, logoutTargets.length - 1)] || 0;
            }
            const targets = [15, 55, 85];
            return targets[Math.min(this.currentStep, targets.length - 1)] || 0;
        },
        statusMessage() {
            if (this.allDone) {
                return this.mode === 'logout' ? 'Redirecting...' : 'Redirecting to your dashboard...';
            }
            const step = this.steps[Math.min(this.currentStep, this.steps.length - 1)];
            return step ? `${step.label}${this.dots}` : '';
        }
    },
    mounted() {
        this.dotInterval = setInterval(() => {
            this.dots = this.dots.length >= 3 ? '' : `${this.dots}.`;
        }, 500);
    },
    beforeDestroy() {
        clearInterval(this.dotInterval);
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

.l2u-login-redirect {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    background: linear-gradient(160deg, #0D2137 0%, #1C3E61 55%, #163352 100%);
    position: relative;
    overflow: hidden;
}

/* Route paths */
.route-paths-svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    opacity: 0.6;
}

.route-dash { animation: dash 12s linear infinite; }
.route-dash--slow { animation-duration: 15s; }
.route-dash--slower { animation-duration: 18s; }

.waypoint { animation: pulse 3s ease-in-out infinite; }
.waypoint--d1 { animation-delay: 1s; }
.waypoint--d2 { animation-delay: 2s; }
.waypoint--d3 { animation-delay: 0.5s; }

/* Card */
.redirect-card {
    position: relative;
    z-index: 2;
    background: #FFFFFF;
    border-radius: 20px;
    padding: 48px 44px 44px;
    width: 100%;
    max-width: 440px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.25), 0 8px 24px rgba(0,0,0,0.15);
    animation: scaleIn 0.5s ease-out;
    text-align: center;
}

.card-logo {
    height: 40px;
    object-fit: contain;
    margin-bottom: 36px;
}

/* Spinner */
.spinner-ring {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    margin: 0 auto 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    background: rgba(43,147,255,0.06);
    animation: ringPulse 2s ease-out infinite;
}

.spinner-ring.done {
    background: rgba(5,150,105,0.1);
    animation: successPop 0.4s ease-out;
}

.spinner-arc {
    position: absolute;
}

.arc {
    transform-origin: center;
    animation: spin 1.2s linear infinite;
}

/* Text */
.heading {
    font-size: 22px;
    font-weight: 700;
    color: #1C3E61;
    margin-bottom: 6px;
    transition: all 0.3s;
}

.status-text {
    font-size: 14px;
    color: #737474;
    margin-bottom: 32px;
    min-height: 20px;
}

/* Progress bar */
.progress-track {
    width: 100%;
    height: 4px;
    background: #EAF2FB;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 28px;
}

.progress-bar {
    height: 100%;
    border-radius: 4px;
    transition: width 0.6s ease-out;
    background: linear-gradient(90deg, #2B93FF 0%, #4ED2FF 50%, #2B93FF 100%);
    background-size: 200% 100%;
    animation: shimmer 1.5s linear infinite;
}

.progress-bar.done {
    background: #059669;
    animation: none;
}

/* Steps */
.steps {
    display: flex;
    flex-direction: column;
}

.step {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    transition: all 0.3s;
}

.step.active {
    background: rgba(43,147,255,0.04);
}

.step-indicator {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #F4F7FB;
    transition: all 0.3s;
}

.step.active .step-indicator {
    background: rgba(43,147,255,0.08);
}

.step.done .step-indicator {
    background: rgba(5,150,105,0.1);
    animation: successPop 0.3s ease-out;
}

.step-label {
    font-size: 14px;
    font-weight: 400;
    color: #C5CFDD;
    transition: all 0.3s;
    text-align: left;
}

.step.active .step-label {
    font-weight: 500;
    color: #1C3E61;
}

.step.done .step-label {
    color: #1C3E61;
}

.active-dot {
    margin-left: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #2B93FF;
    animation: pulse 1.2s ease-in-out infinite;
}

.bottom-trust {
    position: absolute;
    bottom: 32px;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 13px;
    color: rgba(255,255,255,0.25);
    z-index: 2;
}

/* Animations */
@keyframes dash {
    to { stroke-dashoffset: -200; }
}

@keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.8; }
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

@keyframes scaleIn {
    from { opacity: 0; transform: scale(0.85); }
    to { opacity: 1; transform: scale(1); }
}

@keyframes ringPulse {
    0% { box-shadow: 0 0 0 0 rgba(43,147,255,0.25); }
    70% { box-shadow: 0 0 0 14px rgba(43,147,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(43,147,255,0); }
}

@keyframes successPop {
    0% { transform: scale(0.6); opacity: 0; }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); opacity: 1; }
}

@keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
}

@media only screen and (max-width: 768px) {
    .l2u-login-redirect {
        padding: 24px;
    }

    .redirect-card {
        padding: 36px 28px 32px;
    }
}
</style>
